import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
} from '@mui/material';
import Home from '@mui/icons-material/Home';
import Forum from '@mui/icons-material/Forum';
import Payment from '@mui/icons-material/Payment';
import Download from '@mui/icons-material/Download';
import MenuBook from '@mui/icons-material/MenuBook';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import BugReport from '@mui/icons-material/BugReport';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole } from '../../config/adminPermissions';
import { isQaRole } from '../../config/qaPermissions';
import { useLocaleHandler } from '../../hooks';
import { useTranslation } from 'react-i18next';
import {
  appBarSx,
  BurgerMenu,
  LanguageSelector,
  UserInfoChips,
  UserMenu,
  NavButton,
  SidebarToggle,
} from './header';
import { BG_COLORS } from '../../utils/colors';
import { EXTERNAL_LINKS } from '../../constants/config';

interface PersonalCabinetHeaderProps {
  onDrawerToggle?: () => void;
  /** Сайдбар свёрнут (десктоп). Без обработчика кнопка не рисуется. */
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
}

const PersonalCabinetHeader: React.FC<PersonalCabinetHeaderProps> = ({
  onDrawerToggle,
  sidebarCollapsed = false,
  onToggleSidebar,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currentLocale, changingLocale, handleLocaleChange } = useLocaleHandler();

  const isAdmin = isAdminRole(user?.role);
  const isQa = isQaRole(user?.role);

  return (
    <AppBar position="fixed" elevation={0} sx={appBarSx}>
      <Toolbar>
        <BurgerMenu onDrawerToggle={onDrawerToggle} />
        <SidebarToggle collapsed={sidebarCollapsed} onToggle={onToggleSidebar} />

        {/* Central navigation buttons */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: { xs: 'none', md: 'flex' },
            gap: { md: '4px', lg: '8px', xl: '16px' },
            alignItems: 'center',
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
          }}
        >
          <NavButton
            icon={<Home />}
            label={t('navigation:footer.home')}
            to="/"
          />
          <NavButton
            icon={<Forum />}
            label={t('navigation:footer.community')}
            href={EXTERNAL_LINKS.TELEGRAM_CHAT}
          />
          <NavButton
            icon={<Payment />}
            label={t('navigation:footer.donations')}
            to="/dashboard/payment"
          />
          <NavButton
            icon={<Download />}
            label={t('navigation:footer.download')}
            to="/downloads"
          />
          <NavButton
            icon={<MenuBook />}
            label={t('navigation:menu.wiki')}
            to="/wiki"
          />
          {isAdmin && (
            <>
              <NavButton
                icon={<AdminPanelSettings />}
                label={t('navigation:menu.adminPanel')}
                to="/admin"
              />
            </>
          )}
          {isQa && (
            <NavButton
              icon={<BugReport />}
              label={t('qa:header.panel')}
              to="/qa"
            />
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Right side with user info and auth */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5, md: 2 } }}>
          {user ? (
            <>
              <UserInfoChips />
              <LanguageSelector
                currentLocale={currentLocale}
                changingLocale={changingLocale}
                onLocaleChange={handleLocaleChange}
              />
              <UserMenu />
            </>
          ) : (
            <Button
              component={RouterLink}
              to="/login"
              color="inherit"
              size="small"
              sx={{
                minWidth: { xs: '36px', md: 'auto' },
                px: { xs: 1, md: 2 },
                fontSize: { md: '11px', lg: '12px', xl: '14px' },
                '&:hover': { background: BG_COLORS.GLASS_LIGHT },
              }}
            >
              <AccountCircle sx={{ mr: { xs: 0, md: 1 }, fontSize: { xs: 20, md: 22 } }} />
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                {t('navigation:toolbar.personalCabinet')}
              </Box>
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default PersonalCabinetHeader;
